/**
 * Where the visible part of a content-sized frame is (#2678).
 *
 * The iframe is as tall as its content and scrolls in the Bitrix24 page, so for
 * `position: fixed` boxes the iframe's own viewport is the whole frame: a dialog
 * centred in it sits at the middle of the page, far below the portal's window,
 * and its backdrop blocks input the user cannot even see. The SDK cannot say
 * where the portal's window is (`getScrollSize` measures this document,
 * `scrollParentWindow` only sets), but the browser can: an
 * `IntersectionObserver` on the frame's own root reports, across origins, the
 * part of it that is on screen, in the frame's own coordinates — exactly the
 * ones `position: fixed` is placed in.
 *
 * The observer only calls back when the visible SHARE crosses a threshold, and a
 * window sliding through the middle of a tall frame keeps its share: the reading
 * would stay where it was. So it is also taken afresh — a new observer reports
 * its first reading at once — when the user acts (a click or a key opens a
 * dialog), when a fixed box appears, and every `REFRESH_INTERVAL_MS` while one
 * is open (the portal can be scrolled under a dialog).
 *
 * That part is published as two CSS variables on `<html>`; `index.css` anchors
 * the frame's dialogs, drawers, top and bottom sheets, backdrops and the toast
 * viewport to it. Without a reading (no
 * observer, the frame out of sight) the variables are removed and the CSS falls
 * back to the whole frame, which is how fixed boxes behaved before.
 */

export const FRAME_VIEWPORT_TOP_VAR = '--embedded-viewport-top';
export const FRAME_VIEWPORT_HEIGHT_VAR = '--embedded-viewport-height';

/**
 * Ratios the observer reports at. It only calls back when the visible share
 * crosses one, so the step is the precision of a reading: a frame 10 000 px tall
 * is read to within 50 px, and a dialog is placed by a reading that fresh.
 */
const THRESHOLD_STEPS = 200;
const THRESHOLDS = Array.from(
  { length: THRESHOLD_STEPS + 1 },
  (_, i) => i / THRESHOLD_STEPS
);

export interface FrameViewport {
  /** Top of the visible part, from the top of the frame, in pixels. */
  top: number;
  height: number;
}

/** The visible part of the frame in `entry`, or `null` when none of it is on screen. */
export function frameViewportOf(entry: IntersectionObserverEntry): FrameViewport | null {
  if (!entry.isIntersecting) return null;
  const { top, height } = entry.intersectionRect;
  if (!(height > 0)) return null;
  return { top: Math.max(0, Math.round(top)), height: Math.round(height) };
}

/** Set only on a change: a drawer that transitions `top` would glide after every write. */
function setVar(root: HTMLElement, name: string, value: string | null): void {
  if (root.style.getPropertyValue(name) === (value ?? '')) return;
  if (value === null) root.style.removeProperty(name);
  else root.style.setProperty(name, value);
}

function publish(root: HTMLElement, viewport: FrameViewport | null): void {
  setVar(root, FRAME_VIEWPORT_TOP_VAR, viewport ? `${viewport.top}px` : null);
  setVar(root, FRAME_VIEWPORT_HEIGHT_VAR, viewport ? `${viewport.height}px` : null);
}

/** A box `index.css` anchors to the visible part. */
const ANCHORED_SELECTOR = '.fixed.inset-0, [data-frame-anchor]';
/**
 * A box that is positioned elsewhere (a toast, in the always-mounted toast
 * viewport `data-frame-edge`) but must be followed while it is on screen: the
 * reading is kept fresh as the portal scrolls. Never held invisible
 * (`FRAME_VIEWPORT_PENDING_ATTRIBUTE` hides everything anchored), because a toast
 * appears while a dialog may be open and the dialog must not blink.
 */
const FOLLOWED_SELECTOR = '[data-frame-follow]';
const ANY_BOX_SELECTOR = `${ANCHORED_SELECTOR}, ${FOLLOWED_SELECTOR}`;
const REFRESH_INTERVAL_MS = 250;

/**
 * On `<html>` from the moment an anchored box appears until a reading taken after
 * it has arrived: `index.css` keeps the box invisible meanwhile, so it is not
 * painted for a frame at the place the LAST reading names (the portal may have
 * been scrolled since) and then moved. Taken off after `PENDING_LIMIT_MS` in any
 * case — a box must not stay invisible because a reading never came.
 */
export const FRAME_VIEWPORT_PENDING_ATTRIBUTE = 'data-frame-viewport-pending';
const PENDING_LIMIT_MS = 500;

/** One fresh reading of `root`; returns the cancel function. */
function readOnce(
  root: HTMLElement,
  onReading: (viewport: FrameViewport | null) => void
): () => void {
  const observer = new IntersectionObserver((entries) => {
    observer.disconnect();
    const entry = entries[entries.length - 1];
    if (entry) onReading(frameViewportOf(entry));
  });
  observer.observe(root);
  return () => observer.disconnect();
}

function holdsBox(nodes: NodeList, selector: string): boolean {
  return Array.from(nodes).some(
    (node) =>
      node instanceof Element &&
      (node.matches(selector) || node.querySelector(selector) !== null)
  );
}

/**
 * Keep the two variables on `root` at the visible part of the frame; returns
 * the stop function, which also takes the variables off.
 */
export function trackFrameViewport(
  root: HTMLElement = document.documentElement
): () => void {
  if (typeof IntersectionObserver === 'undefined') return () => undefined;
  const doc = root.ownerDocument;
  const observer = new IntersectionObserver(
    (entries) => {
      // Entries are in the order they were observed; the last is the latest.
      const entry = entries[entries.length - 1];
      if (entry) publish(root, frameViewportOf(entry));
    },
    { threshold: THRESHOLDS }
  );
  observer.observe(root);

  let pendingLimit: ReturnType<typeof setTimeout> | null = null;
  const settle = () => {
    if (pendingLimit !== null) clearTimeout(pendingLimit);
    pendingLimit = null;
    root.removeAttribute(FRAME_VIEWPORT_PENDING_ATTRIBUTE);
  };

  let cancelRead: (() => void) | null = null;
  const refresh = () => {
    if (cancelRead) return;
    cancelRead = readOnce(root, (viewport) => {
      cancelRead = null;
      publish(root, viewport);
      settle();
    });
  };

  let timer: ReturnType<typeof setInterval> | null = null;
  const startPolling = () => {
    if (timer === null) timer = setInterval(refresh, REFRESH_INTERVAL_MS);
  };
  const stopPollingWithoutBoxes = () => {
    if (timer !== null && doc.querySelector(ANY_BOX_SELECTOR) === null) {
      clearInterval(timer);
      timer = null;
    }
  };

  const mutations = new MutationObserver((records) => {
    if (records.some((record) => holdsBox(record.addedNodes, ANCHORED_SELECTOR))) {
      // A microtask after the DOM change: before the box is first painted.
      root.setAttribute(FRAME_VIEWPORT_PENDING_ATTRIBUTE, '');
      pendingLimit ??= setTimeout(settle, PENDING_LIMIT_MS);
      refresh();
      startPolling();
    } else if (records.some((record) => holdsBox(record.addedNodes, FOLLOWED_SELECTOR))) {
      refresh();
      startPolling();
    }
    if (records.some((record) => holdsBox(record.removedNodes, ANY_BOX_SELECTOR))) {
      stopPollingWithoutBoxes();
    }
  });
  mutations.observe(doc.body, { childList: true, subtree: true });
  if (doc.querySelector(ANY_BOX_SELECTOR) !== null) startPolling();
  // What opens a dialog: a click, or Enter/Space on a focused control.
  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') refresh();
  };
  doc.addEventListener('pointerdown', refresh, { capture: true, passive: true });
  doc.addEventListener('keydown', onKey, { capture: true, passive: true });

  return () => {
    observer.disconnect();
    mutations.disconnect();
    doc.removeEventListener('pointerdown', refresh, { capture: true });
    doc.removeEventListener('keydown', onKey, { capture: true });
    if (timer !== null) clearInterval(timer);
    cancelRead?.();
    settle();
    publish(root, null);
  };
}
